import { useCallback, useMemo, useState } from "react";
import { getTradeAverages } from "../../../services/Axios";
import { hasActiveFilter, uniqueSymbols } from "./tradeAverages";

// One fetch of the per-symbol averages, shared by the on-page security table
// and the spread popup. Nothing is fetched until load()/ensureFresh() is
// called, and never when the result is not narrowed by a search or filter.
export default function useTradeAverages(query, filters) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalTrades, setTotalTrades] = useState(0);
  const [fetchedKey, setFetchedKey] = useState(null);

  const currentKey = useMemo(
    () => JSON.stringify({ query: query?.trim() || "", filters }),
    [query, filters],
  );
  const canAverage = hasActiveFilter(query, filters);
  const isStale = fetchedKey != null && fetchedKey !== currentKey;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getTradeAverages(query, filters);
      setRows(result.results);
      setTotalTrades(result.totalTrades);
      setFetchedKey(currentKey);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          `Failed to load averages: ${err.message}`,
      );
    } finally {
      setLoading(false);
    }
  }, [query, filters, currentKey]);

  // Reuses what is already loaded when the filters have not changed.
  const ensureFresh = useCallback(() => {
    if (fetchedKey !== currentKey || error) load();
  }, [fetchedKey, currentKey, error, load]);

  const symbols = useMemo(() => uniqueSymbols(rows), [rows]);
  const includedTrades = useMemo(
    () => rows.reduce((sum, r) => sum + r.trades, 0),
    [rows],
  );

  return {
    filterKey: currentKey,
    canAverage,
    loading,
    error,
    rows,
    symbols,
    totalTrades,
    includedTrades,
    isStale,
    loaded: fetchedKey != null,
    load,
    ensureFresh,
  };
}
